import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, BookOpen, Video, Users, Repeat } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { BookClubSettings } from '@/types/chat';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface BookClubSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: BookClubSettings | null;
  isOwner: boolean;
  onUpdate: (updates: Partial<BookClubSettings>) => Promise<void>;
}

const MEETING_FORMATS = [
  { value: 'virtual', label: 'Virtual', icon: Video },
  { value: 'in_person', label: 'In Person', icon: MapPin },
  { value: 'hybrid', label: 'Hybrid', icon: Users },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

const PACE_OPTIONS = [
  'One chapter per week',
  'Whole book per month',
  'Self-paced',
];

const GENRE_OPTIONS = [
  'Fiction', 'Non-Fiction', 'Sci-Fi', 'Fantasy', 'Mystery',
  'Romance', 'Self-Help', 'Biography', 'Horror', 'Thriller',
];

export const BookClubSettingsPanel: React.FC<BookClubSettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  isOwner,
  onUpdate,
}) => {
  const { toast } = useToast();
  const [localSettings, setLocalSettings] = useState<Partial<BookClubSettings>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        meeting_format: settings.meeting_format,
        meeting_frequency: settings.meeting_frequency,
        next_meeting_at: settings.next_meeting_at,
        meeting_location: settings.meeting_location,
        reading_pace: settings.reading_pace,
        genre_focus: settings.genre_focus,
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(localSettings);
      toast({ title: 'Settings saved' });
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleGenre = (genre: string) => {
    const current = localSettings.genre_focus || [];
    const updated = current.includes(genre)
      ? current.filter(g => g !== genre)
      : [...current, genre];
    setLocalSettings(prev => ({ ...prev, genre_focus: updated }));
  };

  const meetingDate = localSettings.next_meeting_at
    ? new Date(localSettings.next_meeting_at)
    : undefined;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Club Settings</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Meeting Format */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Meeting Format</Label>
            <div className="grid grid-cols-3 gap-2">
              {MEETING_FORMATS.map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  type="button"
                  variant={localSettings.meeting_format === value ? 'default' : 'outline'}
                  size="sm"
                  disabled={!isOwner}
                  onClick={() => setLocalSettings(prev => ({ ...prev, meeting_format: value as BookClubSettings['meeting_format'] }))}
                  className="flex items-center gap-1.5"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Meeting Frequency */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How Often</Label>
            <Select
              value={localSettings.meeting_frequency}
              onValueChange={(v) => setLocalSettings(prev => ({ ...prev, meeting_frequency: v as BookClubSettings['meeting_frequency'] }))}
              disabled={!isOwner}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Next Meeting Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Next Meeting</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!isOwner}
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !meetingDate && 'text-muted-foreground'
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {meetingDate ? format(meetingDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarPicker
                  mode="single"
                  selected={meetingDate}
                  onSelect={(d) => setLocalSettings(prev => ({ ...prev, next_meeting_at: d?.toISOString() || null }))}
                  disabled={(d) => d < new Date()}
                  initialFocus
                  className={cn('p-3 pointer-events-auto')}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Meeting Location */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Location / Link</Label>
            <Input
              placeholder="e.g. Zoom link or café address"
              value={localSettings.meeting_location || ''}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, meeting_location: e.target.value }))}
              disabled={!isOwner}
            />
          </div>

          {/* Reading Pace */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reading Pace</Label>
            <div className="flex flex-wrap gap-2">
              {PACE_OPTIONS.map(pace => (
                <Badge
                  key={pace}
                  variant={localSettings.reading_pace === pace ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-colors',
                    !isOwner && 'pointer-events-none'
                  )}
                  onClick={() => setLocalSettings(prev => ({
                    ...prev,
                    reading_pace: prev.reading_pace === pace ? null : pace,
                  }))}
                >
                  {pace}
                </Badge>
              ))}
            </div>
          </div>

          {/* Genre Focus */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Genre Focus</Label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map(genre => (
                <Badge
                  key={genre}
                  variant={(localSettings.genre_focus || []).includes(genre) ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-colors',
                    !isOwner && 'pointer-events-none'
                  )}
                  onClick={() => toggleGenre(genre)}
                >
                  {genre}
                </Badge>
              ))}
            </div>
          </div>

          {/* Save */}
          {isOwner && (
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
